import { useState, useRef } from "react"
import { Button, Box, IconButton, Divider, Typography } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AttachFileIcon from "@mui/icons-material/AttachFile"

const FileUpload = ({
  label,
  onUpload,
  id,
  accept = "*",
  multiple = false,
  files = [],
}) => {
  const [selectedFiles, setSelectedFiles] = useState(files)
  const fileInputRef = useRef(null)

  const handleFileChange = async (e) => {
    const file = e.target.files[0]
    const arrayBuffer = await file.arrayBuffer()
    const base64String = convertArrayBufferToBase64(arrayBuffer)
    const newFile = { name: file.name, arrayBuffer: base64String }
    let currentFiles = []

    multiple
      ? (currentFiles = [...selectedFiles, newFile])
      : (currentFiles = [newFile])
    setSelectedFiles(currentFiles)
    onUpload(currentFiles)
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const truncateFileName = (fileName, maxLength = 45) => {
    if (fileName.length <= maxLength) return fileName

    const extension = fileName.split(".").pop()
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf("."))

    // Reserve characters for the extension plus dots and ellipsis
    const reservedLength = extension.length + 6 // ... + . + extension
    const truncatedLength = maxLength - reservedLength

    return `${nameWithoutExt.slice(0, truncatedLength)}...${extension}`
  }

  const convertArrayBufferToBase64 = (arrayBuffer) => {
    const uint8Array = new Uint8Array(arrayBuffer)
    const binaryString = uint8Array.reduce(
      (acc, byte) => acc + String.fromCharCode(byte),
      ""
    )
    return btoa(binaryString)
  }

  return (
    <div className="formRow" style={{ width: "100%" }}>
      <input
        accept={accept}
        style={{ display: "none" }}
        id={id}
        multiple={multiple}
        type="file"
        onChange={handleFileChange}
        ref={fileInputRef}
      />
      <Box
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ mb: 1, textAlign: "center" }}>
          {label}
        </Typography>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, margin: 0 }}>
            Attached Files
          </Typography>
          {accept && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                ml: 1,
                mb: 1,
                flexGrow: 1,
                textAlign: "right",
                margin: 0,
              }}
            >
              <b>Allowed Files:</b> {accept === "*" ? "All" : accept}
            </Typography>
          )}
        </div>
        {selectedFiles.map((file, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              "&:last-child": {
                mb: 0,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                mr: 2,
                minWidth: 0,
              }}
            >
              <AttachFileIcon
                sx={{
                  fontSize: 20,
                  color: "primary.main",
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
                title={file.name}
              >
                {truncateFileName(file.name)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                mr: 2,
                flexShrink: 0,
              }}
            >
              {formatFileSize(file.arrayBuffer.length)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                const newFiles = selectedFiles.filter((_, i) => i !== index)
                setSelectedFiles(newFiles)
                onUpload(newFiles)
              }}
              sx={{
                color: "error.main",
                flexShrink: 0,
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Divider sx={{ my: 2 }} />
        <label htmlFor={id}>
          <Button
            variant="outlined"
            component="span"
            fullWidth
            startIcon={<AttachFileIcon />}
            size="small"
            sx={{
              color: "primary.main",
              borderColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.main",
                color: "white",
                borderColor: "primary.main",
              },
            }}
            disabled={
              selectedFiles.length >= 5 ||
              (!multiple && selectedFiles.length === 1)
            } // Limit to 5 files
          >
            Upload Files
          </Button>
        </label>
      </Box>
    </div>
  )
}

export default FileUpload
